import React, { useEffect, useState } from "react";
import { Button, Card, Form, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Tooltip } from "bootstrap";
import Swal from "sweetalert2";
import API_BASE_URL from "../config";
// Set axios default headers
axios.defaults.withCredentials = true; // Ensure credentials are sent with requests
axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem(
  "token",
)}`; // Optionally add token if available

axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

export const showToast = (title, icon = "success") => {
  Swal.fire({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    icon,
    title,
  });
};
const EventTypes = () => {
  const [events, setEvents] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isGeneratingMeet, setIsGeneratingMeet] = useState(false);
  const [newEvent, setNewEvent] = useState({
    name: "",
    duration: "",
    description: "",
    visibility: "public",
    is_active: true,
    is_hidden: false,
    username: "", // This will represent the slug part of the URL
    location: "",
    locationDetail: "",
    url: "",
  });
  const [eventToDelete, setEventToDelete] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/events/companies`, { withCredentials: true })
      .then((res) => {
        const comps = res.data.companies || [];
        setCompanies(comps);
        if (comps.length > 0) {
          setSelectedCompany(comps[0]);
          // Optional: pre-fill slug if name already exists
          if (newEvent.name) {
            updateSlug(comps[0], newEvent.name);
          }
        }
      })
      .catch((err) => {
        console.error("Failed to load companies", err);
        showToast("Could not load your companies", "error");
      });
  }, []);

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/events`, { withCredentials: true })
      .then((res) => {
        setEvents(res.data.events);
      })
      .catch((err) => {
        console.error("Failed to fetch events", err);
      });

    const tooltipTriggerList = document.querySelectorAll(
      '[data-bs-toggle="tooltip"]',
    );
    tooltipTriggerList.forEach((tooltipTriggerEl) => {
      new Tooltip(tooltipTriggerEl);
    });
  }, []);
  useEffect(() => {
    // Re-init tooltips when modal opens or button is added
    const timer = setTimeout(() => {
      const tooltipTriggerList = document.querySelectorAll(
        '[data-bs-toggle="tooltip"]',
      );
      tooltipTriggerList.forEach((el) => new Tooltip(el));
    }, 100);

    return () => clearTimeout(timer);
  }, [showCreateModal, isGeneratingMeet]);
  // 2. HELPER: Update slug + name together
  const updateSlug = (company, eventName) => {
    if (!company || !eventName) {
      setNewEvent((prev) => ({ ...prev, url: "" }));
      return;
    }

    const clean = (s) => s.trim().replace(/\s+/g, "-").toLowerCase();
    const eventPart = clean(eventName);
    const fullSlug = `${company.slug}/${eventPart}`;

    setNewEvent((prev) => ({
      ...prev,
      name: eventName, // <-- critical: keep name in sync
      url: fullSlug,
    }));
  };

  const handleCompanyChange = (e) => {
    const companyId = e.target.value;
    const company = companies.find((c) => c.id == companyId);
    setSelectedCompany(company);

    // Re-generate slug with current event name
    if (newEvent.name) {
      updateSlug(company, newEvent.name);
    }
  };

  const toggleVisibility = (id) => {
    axios
      .patch(`${API_BASE_URL}/events/${id}/toggle-active`, null, {
        withCredentials: true,
      })
      .then(() => {
        setEvents(
          events.map((e) =>
            e.id === id ? { ...e, is_active: !e.is_active } : e,
          ),
        );
      })
      .catch((err) => {
        console.error("Failed to toggle active status", err);
      });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // If the user is typing the event name, automatically update the username (slug)
    if (name === "name") {
      updateSlug(selectedCompany, value);
    } else {
      setNewEvent((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleCreateEvent = () => {
    //const eventUrl = `https://ticktickgo.in/${newEvent.username}/`;
    axios
      .post(
        `${API_BASE_URL}/events`,
        {
          name: newEvent.name,
          description: newEvent.description,
          duration: newEvent.duration,
          url: newEvent.url,
          location: newEvent.location,
          locationDetail: newEvent.locationDetail, // Sending location details
          company_id: selectedCompany.id,
        },
        { withCredentials: true },
      )
      .then(() => {
        // Refresh events
        return axios.get(`${API_BASE_URL}/events`, {
          withCredentials: true,
        });
      })
      .then((res) => {
        setEvents(res.data.events);
        setShowCreateModal(false);
        setNewEvent({
          name: "",
          duration: "",
          visibility: "public",
          is_active: true,
          is_hidden: false,
          username: "",
          location: "",
          locationDetail: "", // Reset location and detail
        });
      })
      .catch((err) => {
        const errorMsg = err.response?.data?.error || "Error creating event.";
        showToast(errorMsg, "error");
        console.error("Failed to create event", err);
      });
  };

  const confirmDelete = (event) => {
    setEventToDelete(event);
    setShowDeleteModal(true);
  };

  const handleDeleteEvent = () => {
    axios
      .delete(`${API_BASE_URL}/events/${eventToDelete.id}`, {
        withCredentials: true,
      })
      .then(() => {
        setEvents(events.filter((e) => e.id !== eventToDelete.id));
        setShowDeleteModal(false);
        setEventToDelete(null);
      })
      .catch((err) => {
        console.error("Failed to delete event", err);
      });
  };

  const handleCopyLink = (slug) => {
    const baseUrl = window.location.origin; // Gets http://localhost:3000 or https://ticktickgo.in
    const eventLink = `${baseUrl}/events/${slug}/`;

    navigator.clipboard
      .writeText(eventLink)
      .then(() => showToast("Link copied to clipboard!"))
      .catch(() => showToast("Failed to copy link.", "error"));
  };
  const handleEditEvent = (id) => {
    navigate(`/event/edit/${id}`);
  };
  const handleGenerateMeetLink = async () => {
    if (!selectedCompany) {
      showToast("Please select a company first", "warning");
      return;
    }
    if (!newEvent.name?.trim()) {
      showToast("Please enter an event name first", "warning");
      return;
    }
    setIsGeneratingMeet(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/create_meet_link`,
        {
          summary: newEvent.name.trim() || "Client Meeting",
          start: null, // Let backend use now + 1h
          end: null,
        },
        { withCredentials: true },
      );

      if (response.data.success && response.data.meet_url) {
        setNewEvent((prev) => ({
          ...prev,
          locationDetail: response.data.meet_url,
        }));
        showToast("Google Meet link generated!", "success");
      } else {
        showToast("Failed to generate link", "error");
      }
    } catch (err) {
      const msg = err.response?.data?.error || "Failed to generate Meet link";
      showToast(msg, "error");
      console.error("Generate Meet Error:", err);
    } finally {
      setIsGeneratingMeet(false);
    }
  };
  return (
    <div className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-4 title-line">
        <h5 className="fw700 mb-0 color1">Event Types</h5>
        <Button variant="success" onClick={() => setShowCreateModal(true)}>
          <i class="bi bi-plus-circle me-1"></i> New
        </Button>
      </div>
      <div className="row">
        {events?.map((event) => (
          <div className="col-md-4 col-12">
            <Card key={event.id} className="mb-3">
              <Card.Body className="d-flex flex-column gap-1 p-0">
                <div className="tetbx">
                  <p className="mb-3 color1 fw700 tthree-lines">
                    {" "}
                    {event.description}
                  </p>
                </div>
                <div className="p-3">
                  <h5 className="mb-0 mt-0">{event.name}</h5>
                  <div className="d-flex justify-content-between mb-0">
                    <p className="mb-0 color1 statsbx">
                      <span className="color2 c-badge">
                        <i className="bi bi-clock me-1"></i>Duration
                      </span>{" "}
                      {event.duration}
                    </p>
                    <p className="mb-0 color1 statsbx">
                      <span className="color2 c-badge">
                        <i className="bi bi-check-circle me-1"></i>Status
                      </span>{" "}
                      {event.is_active ? "Active" : "Inactive"}
                    </p>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between gap-2 cbs">
                  <div className="d-flex justify-content-center align-items-center">
                    <Form.Check
                      type="switch"
                      id={`visibility-${event.id}`}
                      label={event.is_active ? "Active" : "Inactive"}
                      checked={event.is_active}
                      className="custom-switch"
                      onChange={() => toggleVisibility(event.id)}
                    />
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-dark"
                      size="sm"
                      title="Copy Link"
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      onClick={() => handleCopyLink(event.slug || event.id)}
                      className="btn-icon"
                    >
                      <i className="bi bi-link-45deg"></i>
                    </Button>
                    <Button
                      variant="outline-dark"
                      size="sm"
                      title="Edit"
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      onClick={() => handleEditEvent(event.id)}
                      className="btn-icon"
                    >
                      <i className="bi bi-pencil-fill"></i>
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      title="Delete"
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      onClick={() => confirmDelete(event)}
                      className="btn-icon"
                    >
                      <i className="bi bi-trash-fill"></i>
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>
        ))}
      </div>
      {/* Create Event Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create New Event</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mt-3">
              <Form.Label>Select Company</Form.Label>
              <Form.Select
                value={selectedCompany?.id || ""}
                onChange={handleCompanyChange}
              >
                <option value="">Choose company</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group controlId="eventName">
              <Form.Label>Event Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={newEvent.name}
                onChange={handleInputChange}
                placeholder="Enter event name"
              />
            </Form.Group>

            <Form.Group controlId="eventDuration" className="mt-3">
              <Form.Label>Duration (in minutes)</Form.Label>
              <Form.Control
                type="number"
                name="duration"
                value={newEvent.duration}
                onChange={handleInputChange}
                min="1" // Minimum value for duration
              />
            </Form.Group>

            <Form.Group controlId="eventUsername" className="mt-3">
              <Form.Label>Meeting URL</Form.Label>
              <Form.Control
                type="text"
                name="username"
                value={`https://ticktickgo.in/${newEvent.url}/`}
                disabled
              />
            </Form.Group>
            <Form.Group controlId="eventVisibility" className="mt-3">
              <Form.Label>Visibility</Form.Label>
              <Form.Select
                name="visibility"
                value={newEvent.visibility}
                onChange={handleInputChange}
              >
                <option value="public">Public</option>
                <option value="private">Private</option>
                <option value="hidden">Hidden</option>
              </Form.Select>
            </Form.Group>
            <Form.Group controlId="eventLocation" className="mt-3">
              <Form.Label>Location</Form.Label>
              <Form.Select
                name="location"
                value={newEvent.location}
                onChange={handleInputChange}
              >
                <option value="">Select a location</option>
                <option value="TickTickGo Meet">TickTickGo Google Meet</option>
                <option value="zoom">TickTickGo Zoom</option>
                <option value="google">Google Meet</option>
                {/* <option value="teams">Microsoft Teams</option>
                <option value="webex">Webex</option>
                <option value="gotomeeting">GoToMeeting</option> */}
                <option value="Meeting Link">Custom Meeting Link</option>
                <option value="In-Person">In-Person</option>
                <option value="Phone">Phone</option>
              </Form.Select>
            </Form.Group>

            {/* Conditional Input based on Location */}
            {newEvent.location && !["TickTickGo Meet", "google", "zoom", "teams", "webex", "gotomeeting"].includes(newEvent.location) && (
              <Form.Group
                controlId="eventLocationDetail"
                className="mt-3 position-relative"
              >
                <Form.Label>Provide the {newEvent.location} details</Form.Label>
                <div className="d-flex align-items-center gap-2">
                  <Form.Control
                    type="text"
                    name="locationDetail"
                    value={newEvent.locationDetail || ""}
                    onChange={handleInputChange}
                    placeholder={`Enter ${newEvent.location} details`}
                  />{" "}
                </div>
              </Form.Group>
            )}
            <Form.Group controlId="eventDescription" className="mt-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                type="text"
                name="description"
                value={newEvent.description}
                onChange={handleInputChange}
                placeholder="Enter event description"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleCreateEvent}>
            Create
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Event</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete <strong>{eventToDelete?.name}</strong>
          ?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteEvent}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EventTypes;
